<template>
	<div :class="classes">
		<router-link class="holder" :to="props.to" @click.capture.stop="onClick">
			<div class="image" ref="image" :style="imagesStyles" @mouseenter="hoverImg = true" @mouseleave="hoverImg = false">
				<img :src="plainURL" :alt="props.boxID" class="plain" :style="plainImgStyles" ref="plainImg">
				<img :src="wireframeURL" :alt="props.boxID" class="wireframe" :style="wireImgStyles">
			</div>
			<div class="title" ref="titleHolder">{{props.title}}</div>
		</router-link>
	</div>
</template>

<script setup lang="ts">
import Utils from '@/utils/Utils';
import gsap from 'gsap/all';
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, type StyleValue } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

const props = defineProps<{
	boxID:string;
	title?:string;
	to:RouteLocationRaw;
}>();

const emit = defineEmits<{
	close: [to:RouteLocationRaw];
}>();

const titleHolder = useTemplateRef<HTMLDivElement>('titleHolder');
const plainImg = useTemplateRef<HTMLImageElement>('plainImg');

const hoverImg = ref<boolean>(false);
const closing = ref<boolean>(false);

const mousePercent = ref<{x:number, y:number}>({x:0, y:0});
const mousePercent_eased = ref<{x:number, y:number}>({x:0, y:0});
const mouseImageX = ref<number>(-.2);
const mouseImageX_eased = ref<number>(0);
const hoverSizePercent = ref<number>(.2);

let disposed:boolean = false;

const classes = computed<string[]>(() => {
	const res = ["homebox"];
	res.push(props.boxID);
	if(hoverImg.value) res.push("hover")
	return res;
});

const plainURL = computed<string>(() => new URL(`../assets/images/boxes/${props.boxID}.png`, import.meta.url).href);

const wireframeURL = computed<string>(() => new URL(`../assets/images/boxes/${props.boxID}_wireframe.png`, import.meta.url).href);

const plainImgStyles = computed<StyleValue>(() => {
	// const percent = mouseImageX_eased.value * 100;
	return {
		// "clip-path": `polygon(0 0, ${percent}% 0, ${percent}% 100%, 0 100%)`,
	}
});

const wireImgStyles = computed<StyleValue>(() => {
	const size = hoverSizePercent.value * 100;
	const percent = mouseImageX_eased.value * 100 - size/2;
	return {
		"clip-path": `polygon(${percent}% 0, ${percent + size}% 0, ${percent + size}% 100%, ${percent}% 100%)`,
	}
});

const imagesStyles = computed<StyleValue>(() => {
	const rx = (mousePercent_eased.value.y) * 10;
	const ry = (mousePercent_eased.value.x-.5) * 2;
	return {
		transform:`rotateX(${rx}deg) rotateZ(${ry}deg)`
	}
});

onMounted(() => {
	document.addEventListener("mousemove", onMouseMove);
	document.addEventListener("touchmove", onMouseMove);

	renderFrame();
});

onBeforeUnmount(() => {
	disposed = true;
	document.removeEventListener("mousemove", onMouseMove);
	document.removeEventListener("touchmove", onMouseMove);
});

async function onClick(e:MouseEvent):Promise<void> {
	closing.value = true;
	e.stopPropagation();
	e.preventDefault();
	onMouseMove(e);

	mouseImageX_eased.value = mouseImageX.value;

	gsap.to(hoverSizePercent, {value:2, duration:1, ease:"sine.inOut", delay:.15});
	gsap.to(titleHolder.value!, {scale:1.35, duration:2, filter:"blur(10px)", ease:"sine.inOut", opacity:0});

	await Utils.promisedTimeout(500);
	emit("close", props.to);

}

function open():void {
	gsap.from(titleHolder.value!, {duration:1, y:50, delay:1, opacity:0})
}

function openFromLeft():void {
	gsap.killTweensOf(titleHolder.value!);
	gsap.fromTo(titleHolder.value!,
		{x:"3em", opacity:0},
		{duration:1, delay:.75, x:0, opacity:1, ease:"sine.put", clearProps:"all"});
}

function openFromRight():void {
	gsap.killTweensOf(titleHolder.value!);
	gsap.fromTo(titleHolder.value!,
		{x:"-3em", opacity:0},
		{duration:1, delay:.75, x:0, opacity:1, ease:"sine.put", clearProps:"all"});
}

function closeToLeft():void {
	gsap.killTweensOf(titleHolder.value!);
	gsap.to(titleHolder.value!, {duration:1, x:"3em", opacity:0, ease:"sine.in", clearProps:"all"});
}

function closeToRight():void {
	gsap.killTweensOf(titleHolder.value!);
	gsap.to(titleHolder.value!, {duration:1, x:"-3em", opacity:0, ease:"sine.in", clearProps:"all"});
}

function onMouseMove(e:MouseEvent|TouchEvent):void {
	const bounds = plainImg.value!.getBoundingClientRect();

	//Element out of screen, ignore it
	if(bounds.x < 0 || bounds.x > document.body.clientWidth) return;

	const pos = {x:0, y:0};
	let onMobile = false;
	if(e instanceof MouseEvent) {
		pos.x = e.clientX;
		pos.y = e.clientY;
	}else{
		pos.x = e.touches[0].clientX;
		pos.y = e.touches[0].clientY;
		onMobile = true;
	}
	mousePercent.value.x = pos.x / document.body.clientWidth;
	mousePercent.value.y = pos.y / document.body.clientHeight;

	const mouseImageY = (pos.y - bounds.y)/bounds.height;
	mouseImageX.value = (pos.x - bounds.x)/bounds.width;

	if(onMobile) {
		//If on mobile, check if finger is hover the image
		//to show the texture image
		hoverImg.value = mouseImageY > 0 && mouseImageY < 1 && mouseImageX.value > 0 && mouseImageX.value < 1;
	}
}

function renderFrame():void {
	if(disposed || closing.value) return;
	requestAnimationFrame(()=>renderFrame());

	const ease = .1;
	mouseImageX_eased.value += (mouseImageX.value - mouseImageX_eased.value) * ease;
	mousePercent_eased.value.x += (mousePercent.value.x - mousePercent_eased.value.x) * ease;
	mousePercent_eased.value.y += (mousePercent.value.y - mousePercent_eased.value.y) * ease;

	if(Math.abs(mouseImageX_eased.value - mouseImageX.value) < .001) mouseImageX_eased.value = mouseImageX.value;
	if(Math.abs(mousePercent_eased.value.x - mousePercent.value.x) < .005) mousePercent_eased.value.x = mousePercent.value.x;
	if(Math.abs(mousePercent_eased.value.y - mousePercent.value.y) < .005) mousePercent_eased.value.y = mousePercent.value.y;
}

defineExpose({ open, openFromLeft, openFromRight, closeToLeft, closeToRight });
</script>

<style scoped lang="less">
.homebox{
	position: relative;

	// &.octopuzz>a>.title{
	// 	top: 40%;
	// }

	// &.flatbox>a>.title{
	// 	top: 45%;
	// }

	&.hover {
		.holder {
			.title {
				// opacity: 0;
				backdrop-filter: blur(2px);
				// background-color: rgba(0, 0, 0, .15);
			}
			.image {
				.plain {
					opacity: 1;
					filter: saturate(100%) contrast(100%);
				}
			}
		}
	}

	.holder {
		text-decoration: none;
		margin: auto;
		display: block;
		width: calc(100% - 15em);
		max-width: 800px;
		// overflow-y: auto;
		// overflow-x: hidden;
		// height: 100%;

		.title {
			.center();
			top: 40%;
			position: absolute;
			
			text-align: center;
			color: @mainColor_normal;
			pointer-events: none;
			font-family: "Megrim";
			font-size: 2.5em;
			padding: .25em;
			
			background-color: rgba(0, 0, 0, .75);
			backdrop-filter: blur(3px);
			transition: backdrop-filter 1s, background-color 1s;

			// z-index: 1;
		}

		.image {
			position: relative;
			user-select: none;

			img {
				width: 100%;
				max-height: 50vh;
				object-fit: contain;
			}

			@p: 100%;
			.plain {
				transition: opacity 1s, filter 1s;
				filter: saturate(0%) contrast(150%);
			}
			.wireframe {
				position: absolute;
				top: 0;
				left: 0;
				// mix-blend-mode: lighten;
				clip-path: polygon(@p 0, 100% 0, 100% 100%, @p 100%);
			}
		}
	}
}
@media only screen and (max-width: 480px) {
.homebox{
	.holder {
		width: calc(100% - 1em);
		.title {
			font-size: 2.5em;
		}

		.image {
			img {
				max-height: 40vh;
			}
		}
	}
}
}
</style>